import { createCriteriaParameters, standardDeleteQuery, standardGetQuery, standardPostQuery, standardPutQuery } from "./apiUtils.js";
import { replacePlaceholdersWithObjValues, setDeleteArrayState, setInsertArrayState } from "../shared/miscUtils";

import { SNACKBAR_SUCCESS_CREATED, SNACKBAR_SUCCESS_DELETED, SNACKBAR_SUCCESS_HIGHLIGHT_REVIEW, SNACKBAR_SUCCESS_UPDATED } from "../../config/language/english";
import * as Constants from "../../config/constants/Constants.js";

/**
 * Function for querying highlight requests with filters, pagination, sorting etc.
 */
export const queryHighlightRequests = (props, options = {}) => {
    const path = Constants.API_HIGHLIGHT_REQUESTS + createCriteriaParameters(options);

    standardGetQuery(props, path, (response) => {
        const states = props.backend.highlightRequests;

        setInsertArrayState(states.setData, response.body.elements);
        states.setQueryLoading(false);

        if (options.pagination === false) return;

        if (options.page !== undefined) states.setQueriedUntil(options.page);
        if (response?.body?.total !== undefined) states.setRowCount(response.body.total);
    });
};

/**
 * Function for querying resources with filters, pagination, sorting etc.
 * NB! This is only meant for resource selection pop-up.
 */
export const querySelectHighlightRequests = (props, states = {}, options = {}) => {
    const params = createCriteriaParameters(options);
    const path = Constants.API_HIGHLIGHT_REQUESTS + params;

    standardGetQuery(props, path, (response) => {
        setInsertArrayState(states.setData, response.body.elements);
    });
};

/**
 * Function for creating a new highlight request.
 */
export const createHighlightRequest = (props, options = {}) => {
    const path = Constants.API_HIGHLIGHT_REQUESTS;

    standardPostQuery(props, path, options.body, (response) => {
        const states = props.backend.highlightRequests;

        setInsertArrayState(states.setData, [{ ...response.body }]);
        states.setRowCount((prev) => prev + 1);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_CREATED });
    });
};

/**
 * Function for updating a highlight request.
 */
export const updateHighlightRequests = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_HIGHLIGHTS_REQUEST_ID, { request_id: options.id });

    standardPutQuery(props, path, options.body, (response) => {
        const states = props.backend.highlightRequests;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for deleting highlight request(s).
 */
export const deleteHighlightRequests = (props, options = {}) => {
    const path = Constants.API_HIGHLIGHT_REQUESTS;

    standardDeleteQuery(props, path, options.body, (response) => {
        const states = props.backend.highlightRequests;

        setDeleteArrayState(states.setData, options.ids);

        states.setRowCount(states.rowCount - options.ids.length);
        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_DELETED });
    });
};

/**
 * Function for approving a highlight request.
 */
export const approveHighlightRequests = (props, options = {}) => {
    standardPutQuery(props, Constants.API_HIGHLIGHT_REQUESTS_APPROVE, options.body, (response) => {
        const states = props.backend.highlightRequests;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for disapproving a highlight request.
 */
export const disapproveHighlightRequests = (props, options = {}) => {
    standardPutQuery(props, Constants.API_HIGHLIGHT_REQUESTS_DISAPPROVE, options.body, (response) => {
        const states = props.backend.highlightRequests;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying a highlight request.
 */
export const retryHighlightRequests = (props, options = {}) => {
    standardPutQuery(props, Constants.API_HIGHLIGHT_REQUESTS_RETRY, options.body, (response) => {
        const states = props.backend.highlightRequests;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for manually starting highlight request queue process.
 */
export const startProcessHighlightRequest = (props, options = {}) => {
    standardPutQuery(props, Constants.API_HIGHLIGHT_REQUESTS_START_PROCESS, undefined, (response) => {
        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for manually stopping highlight request queue process.
 */
export const stopProcessHighlightRequest = (props, options = {}) => {
    standardPutQuery(props, Constants.API_HIGHLIGHT_REQUESTS_STOP_PROCESS, undefined, (response) => {
        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for querying highlights with filters, pagination, sorting etc.
 */
export const queryHighlights = (props, options = {}) => {
    const path = Constants.API_HIGHLIGHTS + createCriteriaParameters(options);

    standardGetQuery(props, path, (response) => {
        const states = props.backend.highlights;

        setInsertArrayState(states.setData, response.body.elements);
        states.setQueryLoading(false);

        if (options.pagination === false) return;

        if (options.page !== undefined) states.setQueriedUntil(options.page);
        if (response?.body?.total !== undefined) states.setRowCount(response.body.total);
    });
};

/**
 * Function for querying highlights that require review.
 */
export const queryHighlightsRequiringReview = (props, options = {}, callback = null) => {
    const highlight_request_ids = options?.row?.highlight_request_ids ?? [];

    const path = Constants.API_HIGHLIGHTS_REQUIRES_REVIEW + (highlight_request_ids.length > 0 ? `?highlight_request_ids=${highlight_request_ids.join(",")}` : "");
    standardGetQuery(props, path, (response) => {
        const states = props.backend.highlights;

        setInsertArrayState(states.setData, response.body);

        if (callback) callback(response);
    });
};

/**
 * Function for querying an existing highlight.
 */
export const queryHighlight = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_HIGHLIGHT_ID, { highlight_id: options.id });

    standardGetQuery(props, path, (response) => {
        const states = props.backend.highlights;

        setInsertArrayState(states.setData, [{ ...response.body }]);
    });
};

/**
 * Function for deleting highlight(s).
 */
export const deleteHighlights = (props, options = {}) => {
    const path = Constants.API_HIGHLIGHTS;

    standardDeleteQuery(props, path, options.body, (response) => {
        const states = props.backend.highlights;

        setDeleteArrayState(states.setData, options.ids);

        states.setRowCount(states.rowCount - options.ids.length);
        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_DELETED });
    });
};

/**
 * Function for approving highlight(s).
 */
export const approveHighlights = (props, options = {}) => {
    standardPutQuery(props, Constants.API_HIGHLIGHTS_APPROVE, options.body, (response) => {
        const states = props.backend.highlights;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for disapproving a highlight(s).
 */
export const disapproveHighlights = (props, options = {}) => {
    standardPutQuery(props, Constants.API_HIGHLIGHTS_DISAPPROVE, options.body, (response) => {
        const states = props.backend.highlights;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for completing a highlight(s).
 */
export const completeHighlights = (props, options = {}) => {
    standardPutQuery(props, Constants.API_HIGHLIGHTS_COMPLETE, options.body, (response) => {
        const states = props.backend.highlights;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for creating a new highlight review.
 */
export const createHighlightReview = (props, options = {}, callback = null) => {
    const path = Constants.API_HIGHLIGHT_REVIEWS;

    standardPostQuery(props, path, options.body, (response) => {
        const { states } = options;

        if (states) {
            setInsertArrayState(states.setData, [{ ...response.body }]);
            states.setRowCount((prev) => prev + 1);
        }

        props.showSnackbar({ message: SNACKBAR_SUCCESS_HIGHLIGHT_REVIEW });
        if (callback) callback(response);
    });
};

/**
 * Function for updating a highlight review.
 */
export const updateHighlightReview = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_HIGHLIGHTS_REVIEW_ID, { review_id: options.id });

    standardPutQuery(props, path, options.body, (response) => {
        const { states } = options;

        if (states) {
            setInsertArrayState(states.setData, [{ ...response.body }]);
            states.setSelected([]);
        }

        props.showSnackbar({ message: SNACKBAR_SUCCESS_HIGHLIGHT_REVIEW });
    });
};

/**
 * Function for deleting highlight review(s).
 */
export const deleteHighlightReviews = (props, options = {}) => {
    const path = Constants.API_HIGHLIGHT_REVIEWS;

    standardDeleteQuery(props, path, options.body, (response) => {
        const { states } = options;

        if (states) {
            setDeleteArrayState(states.setData, options.ids);

            states.setRowCount(states.rowCount - options.ids.length);
            states.setSelected([]);
        }

        props.showSnackbar({ message: SNACKBAR_SUCCESS_DELETED });
    });
};
